import { useReduxState } from 'hooks';
import { getHomePageModulesByType } from 'lib/homepage';
import { ContentLabel, ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  LanguageVariants,
  Select,
  Text,
  TextArea
} from 'components/common/form';

import type { ModuleInSettings } from './HomePageSettingsModules';

type FormValues = {
  data?: object;
  categories?: { id: number; title: string }[];
  description?: string;
  descriptionTranslations?: ModuleInSettings['descriptionTranslations'];
  title?: string;
  titleTranslations?: ModuleInSettings['titleTranslations'];
  limit: number;
};

type ProductType = 'course' | 'path' | 'calendar-event';

type Props = {
  module: ModuleInSettings;
  onUpdate: (module: ModuleInSettings) => void;
} & (
  | {
      children?: never;
      childrenInitialValues?: never;
    }
  | {
      children: React.ReactNode;
      childrenInitialValues: object;
    }
) &
  (
    | {
        hideLimit: true;
        productType?: never;
      }
    | {
        hideLimit?: boolean;
        productType: ProductType;
      }
  );

const HomePageSettingsModulesModuleUpdateSettings = ({
  children,
  childrenInitialValues,
  hideLimit = false,
  module,
  onUpdate,
  productType
}: Props) => {
  const { messages, serverConfig } = useReduxState();

  const handleUpdate = (values: FormValues) => {
    const moduleData = {
      ...module,
      categories: values.categories || [],
      data: values.data || {},
      description: values.description,
      descriptionTranslations: values.descriptionTranslations,
      limit: values.limit,
      title: values.title,
      titleTranslations: values.titleTranslations
    };
    if (!values.limit) {
      moduleData.limit =
        module.courses?.length ||
        module.events?.length ||
        module.paths?.length ||
        0;
    }

    onUpdate(JSON.parse(JSON.stringify(moduleData)));
  };

  const moduleInfo = getHomePageModulesByType({ messages, serverConfig });

  const initialValues = {
    description: module.description,
    descriptionTranslations: module.descriptionTranslations,
    title: module.title,
    titleTranslations: module.titleTranslations,
    limit: module.limit || 4,
    ...childrenInitialValues
  } as FormValues;

  const labelStyle = { minWidth: 200, width: '1%' };

  return (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonIcon="cog"
      buttonSize="header"
      buttonText={messages.commonSettings}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonUpdate}
      modalTitle={messages.commonSettings}
      modalWidth="medium"
      onSubmit={handleUpdate}
      preventSubmit={true}
      pristineIsValid={false}
      style={{ margin: 0, padding: 0 }}
    >
      {() => (
        <>
          <div style={{ fontWeight: 700 }}>{moduleInfo[module.type].title}</div>
          <div style={{ fontWeight: 400, marginTop: 5, marginBottom: 15 }}>
            {moduleInfo[module.type].blurb}
          </div>

          <FormGroup
            label={messages.commonTitle}
            labelIsOptional={true}
            labelTooltip={messages.homePageSettingsModulesTitleInfo}
          >
            <Text
              field="title"
              id="title"
              placeholder={moduleInfo[module.type].title}
            />

            <LanguageVariants field="titleTranslations" type="text" />
          </FormGroup>

          <FormGroup label={messages.commonDescription} labelIsOptional={true}>
            <TextArea
              field="description"
              id="description"
              preventNewLine={true}
              rows={2}
            />

            <LanguageVariants
              field="descriptionTranslations"
              customProps={{ preventNewLine: true, rows: 2 }}
              type="textarea"
            />
          </FormGroup>

          {!hideLimit ? (
            <ContentLabel
              label={
                productType === 'course'
                  ? messages.homePageSettingsModulesNumberOfCourses
                  : productType === 'path'
                  ? messages.homePageSettingsModulesNumberOfPaths
                  : productType === 'calendar-event'
                  ? messages.homePageSettingsModulesNumberOfCalendarEvents
                  : ''
              }
              horizontal={true}
              labelStyle={labelStyle}
            >
              <Select
                field="limit"
                options={[
                  {
                    label: `${messages.homePageSettingsModulesLimitMax} 4`,
                    value: 4
                  },
                  {
                    label: `${messages.homePageSettingsModulesLimitMax} 8`,
                    value: 8
                  },
                  {
                    label: `${messages.homePageSettingsModulesLimitMax} 12`,
                    value: 12
                  },
                  {
                    label: `${messages.homePageSettingsModulesLimitMax} 16`,
                    value: 16
                  }
                ]}
                style={{ width: 200 }}
              />
            </ContentLabel>
          ) : null}

          {children}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default HomePageSettingsModulesModuleUpdateSettings;
